import { Modal, Stack, PasswordInput, Button, Group } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useForm, useController } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { passwordResetSchema, type PasswordResetFormValues } from '../../schemas/teamSchemas';
import { useUpdatePassword } from '../../hooks/useTeamMutations';
import { CPUSER_CONSTRAINTS } from '@/lib/constants/account';
import type { CPUserViewData } from '@/lib/types/Auth';

interface PasswordResetModalProps {
  user: CPUserViewData | null;
  onClose: () => void;
}

export function PasswordResetModal({ user, onClose }: PasswordResetModalProps) {
  const updatePassword = useUpdatePassword();

  const { control, handleSubmit, reset } = useForm<PasswordResetFormValues>({
    resolver: zodResolver(passwordResetSchema),
    defaultValues: { newPassword: '', confirmPassword: '' },
  });

  const passwordField = useController({ name: 'newPassword', control });
  const confirmField = useController({ name: 'confirmPassword', control });

  function handleClose() {
    reset();
    onClose();
  }

  async function onSubmit(values: PasswordResetFormValues) {
    if (!user) return;
    try {
      await updatePassword.mutateAsync({
        cpuserId: user.core.cpuserId,
        req: { newPassword: values.newPassword },
      });
      notifications.show({ title: 'Success', message: 'Password has been reset.', color: 'green' });
      handleClose();
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to reset password.',
        color: 'red',
      });
    }
  }

  return (
    <Modal
      opened={user !== null}
      onClose={handleClose}
      title={`Reset Password — ${user?.core.firstName} ${user?.core.lastName}`}
      size="sm"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap="md">
          <PasswordInput
            label="New Password"
            value={passwordField.field.value}
            onChange={passwordField.field.onChange}
            onBlur={passwordField.field.onBlur}
            error={passwordField.fieldState.error?.message}
            maxLength={CPUSER_CONSTRAINTS.PASSWORD_MAX}
            withAsterisk
          />
          <PasswordInput
            label="Confirm Password"
            value={confirmField.field.value}
            onChange={confirmField.field.onChange}
            onBlur={confirmField.field.onBlur}
            error={confirmField.fieldState.error?.message}
            maxLength={CPUSER_CONSTRAINTS.PASSWORD_MAX}
            withAsterisk
          />
          <Group justify="flex-end">
            <Button variant="outline" onClick={handleClose} disabled={updatePassword.isPending}>
              Cancel
            </Button>
            <Button type="submit" loading={updatePassword.isPending}>
              Reset Password
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
